import { useState, type JSX } from 'react';

import { Input, RadioGroup } from '@/ui';

import type { FieldOption } from '../../types';

const OTHER_VALUE = '__other__';

interface RadioFieldProps {
  id: string;
  label: string | JSX.Element;
  // Plain-text label, for naming the Other input to screen readers.
  labelText: string;
  options: FieldOption[];
  allowOther?: boolean;
  value: string | null;
  onChange: (value: string | null) => void;
  error?: string;
  disabled?: boolean;
  description?: string;
}

const RadioField = ({
  id,
  label,
  labelText,
  options,
  allowOther,
  value,
  onChange,
  error,
  disabled,
  description
}: RadioFieldProps) => {
  const matchesOption = options.some((option) => option.value === value);
  // Tracks that "Other" is the active choice even while its free-text value
  // is empty — otherwise clearing the text would silently fall out of
  // "Other" mode and hide the input.
  const [otherSelected, setOtherSelected] = useState(!!allowOther && !!value && !matchesOption);
  // A value that matches a real option settles the question, whoever set it —
  // so drop the flag rather than let a stale one keep the field in "Other".
  if (otherSelected && (matchesOption || !allowOther)) setOtherSelected(false);
  const isOther = !!allowOther && !matchesOption && (otherSelected || !!value);

  const radioOptions = [
    ...options.map((option) => ({ label: option.label, value: option.value })),
    ...(allowOther ? [{ label: 'Other', value: OTHER_VALUE }] : [])
  ];

  return (
    <div className="flex flex-col gap-2">
      <RadioGroup
        id={id}
        label={label}
        helpText={description}
        disabled={disabled}
        options={radioOptions}
        selectedValue={isOther ? OTHER_VALUE : (value ?? '')}
        onChange={(next) => {
          const chosenOther = next === OTHER_VALUE;
          setOtherSelected(chosenOther);
          onChange(chosenOther ? '' : (next as string));
        }}
      />
      {isOther && (
        <Input
          aria-label={`${labelText} (Other)`}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
      {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default RadioField;
